class RitzNeighborhoodMap {
	constructor(block) {
		this.block = block;
		this.pins = block.querySelectorAll("[data-pin-id]");
		this.items = block.querySelectorAll("[data-target-pin-id]");
		this.categories = block.querySelectorAll(
			".ritz-neighborhood-map__category"
		);
		this.modal = block.querySelector(".ritz-neighborhood-map__modal");
		this.modalContent = block.querySelector(
			".ritz-neighborhood-map__modal-content"
		);
		this.mapViewButton = block.querySelector(
			".ritz-neighborhood-map__map-view-button"
		);
		this.listViewButton = block.querySelector(
			".ritz-neighborhood-map__list-view-button"
		);
		this.overlay = block.querySelector(
			".ritz-neighborhood-map__modal-overlay"
		);
		this.backButton = block.querySelector("[data-map-back]");
		this.nextButton = block.querySelector("[data-map-next]");
		this.mobilePins = block.querySelectorAll("[data-mobile-pin]");
		this.mobilePanels = block.querySelectorAll(
			"[data-mobile-category-panel]"
		);
		this.zoomButtons = block.querySelectorAll("[data-map-zoom]");
		this.mobileAccordionQuery = window.matchMedia("(max-width: 767px)");
		this.zoomLevels = {
			desktop: 1,
			mobile: 1,
		};
		this.panOffsets = {
			desktop: { x: 0, y: 0 },
			mobile: { x: 0, y: 0 },
		};
		this.activePan = null;
		this.activeCategoryIndex = 0;

		this.init();
	}

	init() {
		this.initDesktopHover();
		this.initMobileAccordion();
		this.initMobileModal();
		this.initMapZoom();
		this.initMapPan();
	}

	initDesktopHover() {
		this.items.forEach((item) => {
			const id = item.dataset.targetPinId;
			const pin = this.block.querySelector(`[data-pin-id="${id}"]`);

			if (!pin) return;

			item.addEventListener("mouseenter", () => {
				this.activate(id);
			});

			item.addEventListener("mouseleave", () => {
				this.deactivate(id);
			});

			item.addEventListener("focus", () => {
				this.activate(id);
			});

			item.addEventListener("blur", () => {
				this.deactivate(id);
			});
		});

		this.pins.forEach((pin) => {
			const id = pin.dataset.pinId;
			const item = this.block.querySelector(`[data-target-pin-id="${id}"]`);

			if (!item) return;

			pin.addEventListener("mouseenter", () => {
				this.activate(id);
			});

			pin.addEventListener("mouseleave", () => {
				this.deactivate(id);
			});

			pin.addEventListener("focus", () => {
				this.activate(id);
			});

			pin.addEventListener("blur", () => {
				this.deactivate(id);
			});
		});
	}

	initMobileModal() {
		if (!this.modal || !this.mapViewButton) return;

		this.setActiveMobileCategory(0);

		this.mapViewButton.addEventListener("click", () => {
			this.openModal();
		});

		this.listViewButton?.addEventListener("click", () => {
			this.closeModal();
		});

		this.overlay?.addEventListener("click", () => {
			this.closeModal();
		});

		this.backButton?.addEventListener("click", () => {
			this.goToMobileCategory(-1);
		});

		this.nextButton?.addEventListener("click", () => {
			this.goToMobileCategory(1);
		});

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && this.isModalOpen()) {
				this.closeModal();
			}
		});
	}

	initMobileAccordion() {
		this.categories.forEach((category) => {
			const heading = category.querySelector("h3");

			if (!heading) return;

			heading.setAttribute("role", "button");
			heading.setAttribute("tabindex", "0");
			heading.setAttribute("aria-expanded", "false");

			heading.addEventListener("click", () => {
				this.toggleMobileAccordion(category);
			});

			heading.addEventListener("keydown", (event) => {
				if (event.key !== "Enter" && event.key !== " ") return;

				event.preventDefault();
				this.toggleMobileAccordion(category);
			});
		});

		this.updateMobileAccordionState();
		this.mobileAccordionQuery.addEventListener("change", () => {
			this.updateMobileAccordionState();
		});

		window.addEventListener("resize", () => {
			if (!this.mobileAccordionQuery.matches) return;

			this.categories.forEach((category) => {
				if (category.classList.contains("is-open")) {
					this.openMobileAccordion(category);
				}
			});
		});
	}

	updateMobileAccordionState() {
		this.categories.forEach((category) => {
			if (this.mobileAccordionQuery.matches) {
				this.closeMobileAccordion(category);
				return;
			}

			category.classList.remove("is-open");

			const heading = category.querySelector("h3");
			const list = category.querySelector("ul");

			heading?.setAttribute("aria-expanded", "false");
			if (list) list.style.maxHeight = "";
		});
	}

	toggleMobileAccordion(category) {
		if (!this.mobileAccordionQuery.matches) return;

		const isOpen = category.classList.contains("is-open");

		this.categories.forEach((item) => {
			if (item !== category) {
				this.closeMobileAccordion(item);
			}
		});

		if (isOpen) {
			this.closeMobileAccordion(category);
			return;
		}

		this.openMobileAccordion(category);
	}

	openMobileAccordion(category) {
		const heading = category.querySelector("h3");
		const list = category.querySelector("ul");

		if (!list) return;

		category.classList.add("is-open");
		heading?.setAttribute("aria-expanded", "true");
		list.style.maxHeight = `${list.scrollHeight}px`;
	}

	closeMobileAccordion(category) {
		const heading = category.querySelector("h3");
		const list = category.querySelector("ul");

		category.classList.remove("is-open");
		heading?.setAttribute("aria-expanded", "false");
		if (list) list.style.maxHeight = "0px";
	}

	initMapZoom() {
		this.zoomButtons.forEach((button) => {
			button.addEventListener("click", () => {
				this.zoomMap(button.dataset.mapZoomContext, button.dataset.mapZoom);
			});
		});

		Object.keys(this.zoomLevels).forEach((context) => {
			this.updateMapZoom(context);
		});
	}

	zoomMap(context, direction) {
		if (!context || !this.zoomLevels[context]) return;

		const step = direction === "in" ? 0.25 : -0.25;
		const nextZoom = Math.min(
			2,
			Math.max(1, this.zoomLevels[context] + step)
		);

		this.zoomLevels[context] = nextZoom;

		if (nextZoom <= 1) {
			this.panOffsets[context] = { x: 0, y: 0 };
		}

		this.updateMapZoom(context);
	}

	updateMapZoom(context) {
		const target = this.block.querySelector(
			`[data-map-zoom-target="${context}"]`
		);

		if (!target) return;

		const zoom = this.zoomLevels[context];
		const pan = this.panOffsets[context];

		target.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
		target.classList.toggle("is-zoomed", zoom > 1);

		this.block
			.querySelectorAll(`[data-map-zoom-context="${context}"]`)
			.forEach((button) => {
				button.disabled =
					(button.dataset.mapZoom === "in" && zoom >= 2) ||
					(button.dataset.mapZoom === "out" && zoom <= 1);
			});
	}

	initMapPan() {
		this.block.querySelectorAll("[data-map-zoom-target]").forEach((target) => {
			const context = target.dataset.mapZoomTarget;

			target.addEventListener("pointerdown", (event) => {
				if (this.zoomLevels[context] <= 1) return;

				event.preventDefault();
				target.setPointerCapture(event.pointerId);
				target.classList.add("is-dragging");
				this.activePan = {
					context,
					target,
					pointerId: event.pointerId,
					startX: event.clientX,
					startY: event.clientY,
					originX: this.panOffsets[context].x,
					originY: this.panOffsets[context].y,
				};
			});

			target.addEventListener("pointermove", (event) => {
				if (
					!this.activePan ||
					this.activePan.pointerId !== event.pointerId ||
					this.activePan.target !== target
				) {
					return;
				}

				this.panOffsets[context] = {
					x: this.activePan.originX + event.clientX - this.activePan.startX,
					y: this.activePan.originY + event.clientY - this.activePan.startY,
				};
				this.updateMapZoom(context);
			});

			const endPan = (event) => {
				if (
					!this.activePan ||
					this.activePan.pointerId !== event.pointerId ||
					this.activePan.target !== target
				) {
					return;
				}

				target.classList.remove("is-dragging");
				this.activePan = null;
			};

			target.addEventListener("pointerup", endPan);
			target.addEventListener("pointercancel", endPan);
		});
	}

	openModal() {
		this.modal.classList.add("is-open");
		this.modal.setAttribute("aria-hidden", "false");
		document.body.classList.add("has-ritz-map-modal-open");
		this.setActiveMobileCategory(this.activeCategoryIndex);
		this.listViewButton?.focus();
	}

	closeModal() {
		this.modal.classList.remove("is-open");
		this.modal.setAttribute("aria-hidden", "true");
		document.body.classList.remove("has-ritz-map-modal-open");
		this.mapViewButton?.focus();
	}

	isModalOpen() {
		return this.modal?.classList.contains("is-open");
	}

	goToMobileCategory(direction) {
		const total = this.mobilePanels.length;

		if (!total) return;

		const nextIndex =
			(this.activeCategoryIndex + direction + total) % total;

		this.setActiveMobileCategory(nextIndex);
	}

	setActiveMobileCategory(index) {
		this.activeCategoryIndex = index;

		this.mobilePins.forEach((pin) => {
			pin.classList.toggle(
				"is-active-category",
				Number(pin.dataset.categoryIndex) === index
			);
		});

		this.mobilePanels.forEach((panel) => {
			panel.classList.toggle(
				"is-active",
				Number(panel.dataset.categoryIndex) === index
			);
		});
	}

	activate(id) {
		const pin = this.block.querySelector(`[data-pin-id="${id}"]`);
		const item = this.block.querySelector(`[data-target-pin-id="${id}"]`);

		pin?.classList.add("is-active");
		item?.classList.add("is-active");
	}

	deactivate(id) {
		const pin = this.block.querySelector(`[data-pin-id="${id}"]`);
		const item = this.block.querySelector(`[data-target-pin-id="${id}"]`);

		pin?.classList.remove("is-active");
		item?.classList.remove("is-active");
	}
}

document.querySelectorAll(".ritz-neighborhood-map").forEach((block) => {
	new RitzNeighborhoodMap(block);
});
